import { RouteFactory } from "@app/routePaths";
import { AdvertisementTypeEnum } from "@app/types/enums";
import { PodcastPageContext } from "@app/types/Page";
import { BlockTitle } from "@blocks/Blocks";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Container from "@components/Container/Container";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import MetaTags from "@components/MetaTags/MetaTags";
import PodcastsTopFeaturedCarousel from "@components/Slider/PodcastsTopFeaturedCarousel";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import Overview from "@pages/Section/components/Overview/Overview";
import { GoogleAdsSlotFactory } from "@util/helpers";

import PodcastsKeywords from "./components/PodcastsKeywords";
import { PODCAST_KEYWORDS_DATA } from "./util/constants";

export type PodcastPageContentProps = Pick<PodcastPageContext, "data">;

export default function PodcastPageContent({
  data: {
    title,
    podcastsFeaturedArticles,
    marketFocusArticles,
    moneyHacksArticles,
    lensOnArticles,
    wealthBtArticles,
    propertyBtArticles,
    markToMarketArticles,
    brandedContentArticles,
    morePodcastsArticles,
  },
}: PodcastPageContentProps): React.ReactElement {
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting({
      targeting: { key: "page", value: "listing" },
    });

  const slotUniqueName = RouteFactory.podcasts.replace("/", "");
  const termName = "Podcasts";
  const articleMapping = [
    { name: "marketFocus", articles: marketFocusArticles },
    { name: "moneyHacks", articles: moneyHacksArticles },
    { name: "lensOn", articles: lensOnArticles },
    { name: "wealthBT", articles: wealthBtArticles },
    { name: "propertyBT", articles: propertyBtArticles },
    { name: "markToMarket", articles: markToMarketArticles },
    { name: "brandedContent", articles: brandedContentArticles },
  ];

  return (
    <>
      <MetaTags
        title={title}
        description={`THE BUSINESS TIMES ${termName} - Find ${termName} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="website"
        slug={RouteFactory.podcasts}
      />

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.LB1,
          slot: GoogleAdsSlotFactory.lb1(slotUniqueName),
        }}
      />

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial(slotUniqueName)}
        {...{ isPrestitialEnabled, topOverlayImpressions, topOverlayValidity }}
      />

      <Header />

      <Container>
        <Row rootClassName="py-10">
          <Column rootClassName="w-full pb-10 lg:w-8/12 lg:pb-0">
            <PodcastsTopFeaturedCarousel data={podcastsFeaturedArticles} />
          </Column>

          <Column rootClassName="w-full lg:w-4/12">
            <Advertisement
              rootClassName="lg:sticky lg:top-[120px]"
              adUnitProps={{
                type: AdvertisementTypeEnum.IMU1,
                slot: GoogleAdsSlotFactory.imu1(slotUniqueName),
              }}
            />
          </Column>
        </Row>

        <>
          {PODCAST_KEYWORDS_DATA.map(
            ({ apple_link, description, name, spotify_link, tag }) => {
              // Gets the appropriate article list based on mapping.
              const articles = articleMapping
                .filter(({ name: _name }) => {
                  return name === _name;
                })
                .flatMap(({ articles }) => {
                  return articles;
                });
              const blockTitle = tag.name.toUpperCase();

              return (
                <PodcastsKeywords
                  key={name}
                  rootClassName="mb-8 border-t border-black pt-3"
                  {...{
                    blockTitle,
                    articles,
                    description,
                    spotify_link,
                    apple_link,
                    tag,
                  }}
                />
              );
            }
          )}
        </>

        <Row>
          <Column rootClassName="w-full lg:w-8/12">
            <BlockTitle
              text="MORE PODCASTS"
              rootClassName="border-t border-black pt-3"
            />

            <Overview data={morePodcastsArticles} />
          </Column>

          <Column rootClassName="w-full lg:w-4/12">
            <Advertisement
              rootClassName="lg:sticky lg:top-[120px]"
              adUnitProps={{
                type: AdvertisementTypeEnum.IMU2,
                slot: GoogleAdsSlotFactory.imu2(slotUniqueName),
              }}
            />
          </Column>
        </Row>
      </Container>

      <FooterSubscribeRow />
    </>
  );
}
